'use client';

import React from 'react';
import { Check, ChevronRight } from 'lucide-react';
import { api } from '~/utils/api';
import { toast } from '~/utils/toast';
import HoverClickPopout from '~/components/HoverClickPopout';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { useLocalStorage, localStorageKeys } from '~/hooks/useLocalStorage';
import TruncatedText from '~/components/TruncatedText';
import { cn } from '~/lib/utils';
import { NavPopoverShell } from './NavPopoverShell';

export default function WorkspaceSwitcher() {
  const { workspaceId, setWorkspaceId, setWorkspaceName, workspaceName } = useWorkspace();
  const [storedWorkspaceId, setStoredWorkspaceId] = useLocalStorage(
    localStorageKeys.currentWorkspaceId,
    null,
  );
  const utils = api.useUtils();

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Match layout-client.tsx's options for this shared query key: a shorter staleTime
  // here would force a refetch of the whole app shell's workspace list on mount.
  const { isLoading, data } = api.workspace.getUserWorkspaces.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const switchWorkspace = async (newWorkspaceId: string) => {
    if (!pathname) return;

    const segments = pathname.split('/').filter(Boolean);
    if (segments.length === 0) return;

    segments[0] = newWorkspaceId;

    const query = searchParams?.toString();
    const nextUrl = '/' + segments.join('/') + (query ? `?${query}` : '');

    await utils.user.authorize.invalidate();
    router.push(nextUrl);
  };

  if (isLoading) {
    return null;
  }

  const activeWorkspaceId = storedWorkspaceId ?? workspaceId;

  const activeWorkspaceName = !!workspaceName.trim().length
    ? workspaceName
    : data?.find((w) => w.workspaceId === activeWorkspaceId)?.workspace.name;

  if (data && data.length <= 1) {
    return (
      <div>
        <div className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">
          Workspace
        </div>
        <TruncatedText as="div" className="text-foreground mt-1 text-sm">
          {activeWorkspaceName}
        </TruncatedText>
      </div>
    );
  }

  return (
    <HoverClickPopout
      fullWidth
      openOnHover={false}
      width="220px"
      offset={6}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      content={
        <NavPopoverShell width={220}>
          <div className="text-muted-foreground border-border border-b px-3 py-2 text-[11px] font-medium tracking-[0.08em] uppercase">
            Select workspace
          </div>
          <div className="space-y-0.5 p-1.5" role="listbox" aria-label="Workspaces">
            {data?.map((userWorkspace) => {
              const isActive = activeWorkspaceId === userWorkspace.workspaceId;
              return (
                <button
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  key={userWorkspace.workspaceId}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                      : 'text-foreground hover:bg-sidebar-accent/60',
                  )}
                  onClick={async () => {
                    setStoredWorkspaceId(userWorkspace.workspaceId);
                    setWorkspaceId(userWorkspace.workspaceId);
                    setWorkspaceName(userWorkspace.workspace.name);
                    toast.success(`Switched to ${userWorkspace.workspace.name}`);
                    await switchWorkspace(userWorkspace.workspaceId);
                  }}
                >
                  <TruncatedText className="flex-1 text-sm">
                    {userWorkspace.workspace.name}
                  </TruncatedText>
                  {isActive ? <Check className="size-4 shrink-0" /> : null}
                </button>
              );
            })}
          </div>
        </NavPopoverShell>
      }
    >
      <button
        type="button"
        className="hover:bg-sidebar-accent/60 group flex w-full items-center justify-between rounded-md px-2 py-2 text-left outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50"
        aria-label={`Workspace: ${activeWorkspaceName ?? 'Select workspace'}`}
      >
        <div className="min-w-0">
          <div className="text-muted-foreground text-[11px] font-medium tracking-[0.08em] uppercase">
            Workspace
          </div>
          <TruncatedText as="div" className="text-foreground mt-1 text-sm">
            {activeWorkspaceName}
          </TruncatedText>
        </div>
        <ChevronRight className="text-muted-foreground ml-2 size-4 shrink-0" />
      </button>
    </HoverClickPopout>
  );
}
